import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface OrgStats {
  organization_id: string;
  organization_name: string;
  library_count: number;
  user_count: number;
  ai_usage_count: number;
}

const Analytics: React.FC = () => {
  const { data: orgStats, isLoading } = useQuery({
    queryKey: ['super-admin-org-analytics'],
    queryFn: async () => {
      // Get all organizations
      const { data: orgs, error: orgsError } = await supabase
        .from('organizations')
        .select('id, name')
        .eq('is_active', true);

      if (orgsError) throw orgsError;

      // Get counts for each org
      const statsPromises = orgs.map(async (org) => {
        const [libraryResult, userResult, aiResult] = await Promise.all([
          supabase.from('library_items').select('id', { count: 'exact', head: true }).eq('organization_id', org.id),
          supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('organization_id', org.id),
          supabase.from('ai_usage_logs').select('id', { count: 'exact', head: true }).eq('organization_id', org.id),
        ]);

        return {
          organization_id: org.id,
          organization_name: org.name,
          library_count: libraryResult.count ?? 0,
          user_count: userResult.count ?? 0,
          ai_usage_count: aiResult.count ?? 0,
        };
      });

      return Promise.all(statsPromises) as Promise<OrgStats[]>;
    }
  });

  const totals = orgStats?.reduce(
    (acc, stat) => ({
      library: acc.library + stat.library_count,
      users: acc.users + stat.user_count,
      ai: acc.ai + stat.ai_usage_count,
    }),
    { library: 0, users: 0, ai: 0 }
  ) ?? { library: 0, users: 0, ai: 0 };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Cross-Org Analytics</h1>
        <p className="text-slate-500">Platform-wide usage statistics by organization</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Total Library Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">{totals.library}</div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Total Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">{totals.users}</div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">AI Usage Logs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">{totals.ai}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-800">Usage by Organization</CardTitle>
          <CardDescription className="text-slate-500">Breakdown of platform usage per tenant</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-slate-500">Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead className="text-right">Library Items</TableHead>
                  <TableHead className="text-right">Users</TableHead>
                  <TableHead className="text-right">AI Usage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orgStats?.map((stat) => (
                  <TableRow key={stat.organization_id}>
                    <TableCell className="font-medium">{stat.organization_name}</TableCell>
                    <TableCell className="text-right">{stat.library_count}</TableCell>
                    <TableCell className="text-right">{stat.user_count}</TableCell>
                    <TableCell className="text-right">{stat.ai_usage_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
